import React from "react";
import MarkdownBody from "../MarkdownBody";
import { cn } from "@/lib/utils";

function colorToCss(color) {
  if (color == null || !Number.isFinite(Number(color))) return null;
  const n = Math.max(0, Math.min(0xffffff, Math.floor(Number(color))));
  return `#${n.toString(16).padStart(6, "0")}`;
}

/**
 * Discord-style rich card for Circatron inbound embeds.
 */
export default function CircatronEmbedCard({
  embed,
  membersById,
  compact = false,
  className,
}) {
  if (!embed || typeof embed !== "object") return null;

  const title = String(embed.title || "").trim();
  const description = String(embed.description || "").trim();
  const fields = Array.isArray(embed.fields) ? embed.fields : [];
  const imageUrl =
    typeof embed.image_url === "string" &&
    embed.image_url.startsWith("https://")
      ? embed.image_url
      : null;
  const thumbUrl =
    typeof embed.thumbnail_url === "string" &&
    embed.thumbnail_url.startsWith("https://")
      ? embed.thumbnail_url
      : null;
  const bar = colorToCss(embed.color) || "hsl(var(--muted-foreground) / 0.35)";

  if (
    !title &&
    !description &&
    !fields.length &&
    !imageUrl &&
    !thumbUrl
  ) {
    return null;
  }

  const inlineFields = fields.filter((f) => f?.inline);
  const blockFields = fields.filter((f) => !f?.inline);

  return (
    <div
      className={cn(
        "w-fit max-w-md min-w-[min(100%,220px)] rounded-md border border-border/60 bg-background/80 overflow-hidden",
        className
      )}
    >
      <div className="flex min-w-0">
        <div
          className="w-1 shrink-0 self-stretch"
          style={{ backgroundColor: bar }}
          aria-hidden
        />
        <div
          className={cn(
            "flex-1 min-w-0 flex gap-3",
            compact ? "p-2.5" : "p-3"
          )}
        >
          <div className="flex-1 min-w-0 space-y-2">
            {title ? (
              <p className="text-sm font-semibold text-foreground leading-snug break-words">
                {title}
              </p>
            ) : null}
            {description ? (
              <div className="text-sm text-foreground/90 leading-snug break-words">
                <MarkdownBody
                  content={description}
                  compact
                  inverse={false}
                  membersById={membersById}
                />
              </div>
            ) : null}
            {inlineFields.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-3 gap-y-2">
                {inlineFields.map((f, i) => (
                  <div key={`in-${i}`} className="min-w-0">
                    <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide truncate">
                      {f.name}
                    </p>
                    <p className="text-sm text-foreground break-words whitespace-pre-wrap">
                      {f.value}
                    </p>
                  </div>
                ))}
              </div>
            ) : null}
            {blockFields.length > 0 ? (
              <div className="space-y-2">
                {blockFields.map((f, i) => (
                  <div key={`bl-${i}`} className="min-w-0">
                    <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">
                      {f.name}
                    </p>
                    <p className="text-sm text-foreground break-words whitespace-pre-wrap">
                      {f.value}
                    </p>
                  </div>
                ))}
              </div>
            ) : null}
            {imageUrl ? (
              <a
                href={imageUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="block rounded overflow-hidden max-w-full"
              >
                <img
                  src={imageUrl}
                  alt=""
                  className="max-h-64 w-auto max-w-full object-contain rounded"
                  loading="lazy"
                  referrerPolicy="no-referrer"
                />
              </a>
            ) : null}
          </div>
          {thumbUrl ? (
            <a
              href={thumbUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="shrink-0 self-start"
            >
              <img
                src={thumbUrl}
                alt=""
                className="h-16 w-16 object-cover rounded"
                loading="lazy"
                referrerPolicy="no-referrer"
              />
            </a>
          ) : null}
        </div>
      </div>
    </div>
  );
}
